import React, { useState } from 'react';
import { UserRole, getRoleDescription, getRoleColor } from '../types/database';
import { 
  Building2, 
  DollarSign, 
  FileText, 
  Scale, 
  User, 
  Eye, 
  Lightbulb,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

interface OnboardingFormProps {
  onSubmit: (data: OnboardingData) => Promise<void>;
  isLoading?: boolean;
}

export interface OnboardingData {
  role: UserRole;
  firstName: string;
  lastName: string;
  company?: string;
  experience?: string;
  interests?: string[];
}

const ROLE_OPTIONS: Array<{
  value: UserRole;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}> = [
  {
    value: 'Buyer',
    label: 'Buyer',
    description: 'Looking to acquire businesses',
    icon: <Building2 className="w-6 h-6" />,
    color: 'border-purple-200 hover:border-purple-300 bg-purple-50'
  },
  {
    value: 'Lender',
    label: 'Lender',
    description: 'Providing financing for acquisitions',
    icon: <DollarSign className="w-6 h-6" />,
    color: 'border-blue-200 hover:border-blue-300 bg-blue-50'
  },
  {
    value: 'Seller',
    label: 'Seller',
    description: 'Selling or valuing businesses',
    icon: <FileText className="w-6 h-6" />,
    color: 'border-green-200 hover:border-green-300 bg-green-50'
  },
  {
    value: 'Advisor',
    label: 'Advisor',
    description: 'Providing business advisory services',
    icon: <Lightbulb className="w-6 h-6" />,
    color: 'border-orange-200 hover:border-orange-300 bg-orange-50'
  },
  {
    value: 'Attorney',
    label: 'Attorney',
    description: 'Legal services for transactions',
    icon: <Scale className="w-6 h-6" />,
    color: 'border-indigo-200 hover:border-indigo-300 bg-indigo-50'
  },
  {
    value: 'Viewer',
    label: 'Viewer',
    description: 'Read-only access to data',
    icon: <Eye className="w-6 h-6" />,
    color: 'border-gray-200 hover:border-gray-300 bg-gray-50'
  }
];

const EXPERIENCE_OPTIONS = [
  'Less than 1 year',
  '1-3 years',
  '3-5 years',
  '5-10 years',
  '10+ years'
];

const INTEREST_OPTIONS = [
  'Financial Analysis',
  'Deal Structuring',
  'Document Management',
  'Business Valuation',
  'Legal Documentation',
  'Market Research',
  'Due Diligence',
  'Risk Assessment'
];

export function OnboardingForm({ onSubmit, isLoading = false }: OnboardingFormProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<OnboardingData>({
    role: 'Viewer',
    firstName: '',
    lastName: '',
    company: '',
    experience: '',
    interests: []
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateStep = (stepNumber: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (stepNumber === 1) {
      if (!formData.firstName.trim()) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData.lastName.trim()) {
        newErrors.lastName = 'Last name is required';
      }
    }

    if (stepNumber === 2) {
      if (!formData.role) {
        newErrors.role = 'Please select a role';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (validateStep(step)) {
      await onSubmit(formData);
    }
  };

  const handleInterestToggle = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests?.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...(prev.interests || []), interest]
    }));
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome! Let's get started</h2>
        <p className="text-gray-600">First, tell us a bit about yourself</p>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
            First Name *
          </label>
          <input
            type="text"
            id="firstName"
            value={formData.firstName}
            onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.firstName ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter your first name"
          />
          {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
            Last Name *
          </label>
          <input
            type="text"
            id="lastName"
            value={formData.lastName}
            onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.lastName ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter your last name"
          />
          {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
        </div>

        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
            Company (Optional)
          </label>
          <input
            type="text"
            id="company"
            value={formData.company || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your company name"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">What's your role?</h2>
        <p className="text-gray-600">Select the role that best describes your primary function</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {ROLE_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, role: option.value }))}
            className={`p-4 border-2 rounded-lg text-left transition-all ${
              formData.role === option.value
                ? 'border-blue-500 bg-blue-50'
                : option.color
            }`}
          >
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded-lg ${
                formData.role === option.value ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
              }`}>
                {option.icon}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{option.label}</h3>
                <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                <p className="text-xs text-gray-500 mt-2">{getRoleDescription(option.value)}</p>
              </div>
              {formData.role === option.value && (
                <CheckCircle className="w-5 h-5 text-blue-600" />
              )}
            </div>
          </button>
        ))}
      </div>

      {errors.role && <p className="text-red-500 text-sm">{errors.role}</p>}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Tell us about your experience</h2>
        <p className="text-gray-600">Help us customize your experience</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Years of experience in business transactions
        </label>
        <div className="space-y-2">
          {EXPERIENCE_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, experience: option }))}
              className={`w-full p-3 text-left border rounded-lg transition-all ${
                formData.experience === option
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Areas of interest (select all that apply)
        </label>
        <div className="grid grid-cols-2 gap-2">
          {INTEREST_OPTIONS.map((interest) => (
            <button
              key={interest}
              type="button"
              onClick={() => handleInterestToggle(interest)}
              className={`p-2 text-sm border rounded-lg transition-all ${
                formData.interests?.includes(interest)
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {interest}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review your information</h2>
        <p className="text-gray-600">Please review and confirm your details</p>
      </div>

      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <div className="flex justify-between">
          <span className="font-medium text-gray-700">Name:</span>
          <span className="text-gray-900">{formData.firstName} {formData.lastName}</span>
        </div>
        
        {formData.company && (
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Company:</span>
            <span className="text-gray-900">{formData.company}</span>
          </div>
        )}
        
        <div className="flex justify-between">
          <span className="font-medium text-gray-700">Role:</span>
          <span className={`px-2 py-1 rounded-full text-sm ${getRoleColor(formData.role)}`}>
            {formData.role}
          </span>
        </div>
        
        {formData.experience && (
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Experience:</span>
            <span className="text-gray-900">{formData.experience}</span>
          </div>
        )}
        
        {formData.interests && formData.interests.length > 0 && (
          <div>
            <span className="font-medium text-gray-700 block mb-2">Interests:</span>
            <div className="flex flex-wrap gap-2">
              {formData.interests.map((interest) => (
                <span key={interest} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  {interest}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Progress indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Step {step} of 4</span>
            <span className="text-sm text-gray-500">{Math.round((step / 4) * 100)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / 4) * 100}%` }}
            />
          </div>
        </div>

        {/* Step content */}
        <div className="mb-8">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={step === 1}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          {step < 4 ? (
            <button
              type="button"
              onClick={handleNext}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <span>Next</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Setting up...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Complete Setup</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
